/**
 * pk-reorder-mode.js
 *
 * Runs on product pages. When the customer arrives from the cart's stale
 * reorder banner, this script:
 *   1. Reads reorder data (orderRef, changeNotes) from sessionStorage
 *   2. Shows a prominent "Reorder Mode" banner at the top of the PDP
 *   3. Displays the customer's change notes (if any)
 *   4. Intercepts any /cart/add.js or /cart/update.js request and injects
 *      reorder line item properties so the order carries the reorder flag
 *      and change request through to checkout and the CSR-facing admin view
 *
 * Line item properties added:
 *   - Reorder: Yes
 *   - Original Order: <ref> or "Not specified"
 *   - Change Request: <notes> or "No changes \u2014 exact reorder"
 *   - _reorder_flag: 1   (hidden, for programmatic detection / Shopify Flow)
 */
(function () {
  'use strict';

  var REORDER_KEY = 'pk_reorder_data';

  // Only run on product pages
  if (!/\/products\//.test(window.location.pathname)) return;

  var reorderData = readReorderData();
  if (!reorderData || !reorderData.isReorder) return;

  // Expire stale reorder data (older than 1 hour)
  if (reorderData.timestamp && (Date.now() - reorderData.timestamp > 60 * 60 * 1000)) {
    clearReorderData();
    return;
  }

  showReorderBanner();
  interceptAddToCart();

  // ------------------------------------------------------------------

  function readReorderData() {
    try {
      var raw = sessionStorage.getItem(REORDER_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearReorderData() {
    try { sessionStorage.removeItem(REORDER_KEY); } catch (e) {}
  }

  // ------------------------------------------------------------------
  // Visual banner at the top of the product page
  // ------------------------------------------------------------------
  function showReorderBanner() {
    // Avoid double-rendering
    if (document.getElementById('pk-reorder-mode-banner')) return;

    var banner = document.createElement('div');
    banner.id = 'pk-reorder-mode-banner';
    banner.style.cssText = [
      'position:sticky;top:0;z-index:99;',
      'background:#fff8f3;border:2px solid #ff8133;',
      'padding:16px 20px;margin:0 auto 16px;max-width:1200px;',
      'font-family:inherit;'
    ].join('');

    var heading = document.createElement('div');
    heading.style.cssText = 'font-size:16px;font-weight:700;color:#b45309;margin-bottom:6px;text-align:center;';
    var orderRefText = reorderData.orderRef ? ' from Order ' + reorderData.orderRef : '';
    heading.textContent = '\uD83D\uDD04 Reorder Mode' + orderRefText;

    var subtext = document.createElement('div');
    subtext.style.cssText = 'font-size:13px;color:#444;line-height:1.5;text-align:center;';
    subtext.textContent = 'We have your original design on file. Confirm your quantity below and add to cart \u2014 we will process it as an exact reorder unless you have noted changes.';

    banner.appendChild(heading);
    banner.appendChild(subtext);

    // Show change notes if the customer entered any
    var changeNotes = (reorderData.changeNotes || '').trim();
    if (changeNotes) {
      var notesBox = document.createElement('div');
      notesBox.style.cssText = [
        'margin-top:12px;padding:12px 14px;',
        'background:#fff;border:1px solid #ffb27a;',
        'font-size:13px;color:#444;line-height:1.5;'
      ].join('');
      var notesLabel = document.createElement('div');
      notesLabel.style.cssText = 'font-weight:700;color:#b45309;margin-bottom:4px;';
      notesLabel.textContent = 'Your change request:';
      var notesText = document.createElement('div');
      notesText.textContent = changeNotes;
      notesBox.appendChild(notesLabel);
      notesBox.appendChild(notesText);
      banner.appendChild(notesBox);
    }

    // Small "Cancel reorder mode" link
    var cancelWrap = document.createElement('div');
    cancelWrap.style.cssText = 'text-align:center;margin-top:10px;';
    var cancelLink = document.createElement('a');
    cancelLink.href = '#';
    cancelLink.style.cssText = 'font-size:11px;color:#888;text-decoration:underline;';
    cancelLink.textContent = 'Cancel reorder mode';
    cancelLink.addEventListener('click', function (e) {
      e.preventDefault();
      clearReorderData();
      banner.remove();
    });
    cancelWrap.appendChild(cancelLink);
    banner.appendChild(cancelWrap);

    // Insert at the very top of main content (or body if no main)
    var main = document.querySelector('main') || document.body;
    main.insertBefore(banner, main.firstChild);
  }

  // ------------------------------------------------------------------
  // Intercept /cart/add.js and related endpoints to inject reorder
  // line item properties on top of whatever Easefy / the theme sends.
  // ------------------------------------------------------------------
  function interceptAddToCart() {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (isCartAddUrl(url) && init && init.body) {
          init.body = injectProperties(init.body);
        }
      } catch (e) {}
      return origFetch.apply(this, arguments);
    };

    var origXHROpen = XMLHttpRequest.prototype.open;
    var origXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._pkReorderUrl = url || '';
      return origXHROpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this._pkReorderUrl && isCartAddUrl(this._pkReorderUrl) && body) {
          body = injectProperties(body);
        }
      } catch (e) {}
      return origXHRSend.call(this, body);
    };
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  function injectProperties(body) {
    var props = buildReorderProperties();

    // 1) JSON body (most common for modern themes / Easefy)
    if (typeof body === 'string') {
      try {
        var obj = JSON.parse(body);
        if (obj && typeof obj === 'object') {
          mergeProps(obj, props);
          return JSON.stringify(obj);
        }
      } catch (e) {
        // Not JSON — fall through to URL-encoded handling
      }

      // 2) URL-encoded form string
      if (body.indexOf('=') !== -1) {
        try {
          var params = new URLSearchParams(body);
          Object.keys(props).forEach(function (key) {
            params.set('properties[' + key + ']', props[key]);
          });
          return params.toString();
        } catch (e) {}
      }
      return body;
    }

    // 3) FormData
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      Object.keys(props).forEach(function (key) {
        body.set('properties[' + key + ']', props[key]);
      });
      return body;
    }

    // 4) Plain object (defensive)
    if (body && typeof body === 'object') {
      mergeProps(body, props);
      return body;
    }

    return body;
  }

  function mergeProps(obj, props) {
    // Top-level properties (single-item add)
    if (obj.properties && typeof obj.properties === 'object') {
      Object.keys(props).forEach(function (k) { obj.properties[k] = props[k]; });
    } else {
      obj.properties = Object.assign({}, props);
    }
    // items array (multi-item add)
    if (Array.isArray(obj.items)) {
      obj.items.forEach(function (item) {
        item.properties = Object.assign({}, item.properties || {}, props);
      });
    }
  }

  function buildReorderProperties() {
    var notes = (reorderData.changeNotes || '').trim();
    return {
      'Reorder': 'Yes',
      'Original Order': reorderData.orderRef || 'Not specified',
      'Change Request': notes || 'No changes \u2014 exact reorder',
      '_reorder_flag': '1'
    };
  }
}());
